/**
 * §13.1.9 cause-of-death telemetry, gathered presentation-side from the event bus.
 *
 * The UI subscribes read-only; nothing here writes to `World`. A fixed-size ring
 * buffer holds the last 10 seconds of intake so the report can be built at the
 * moment of death without a scan of anything but the pickup pool.
 */

import { enemyById, bossById } from '../core/content';
import type { World } from '../sim/world';
import { humanizeId } from './format';
import type { DefeatReport } from './types';

const RING = 128;
const WINDOW_TICKS = 600; // 10 s @ 60 Hz

interface Intake {
  tick: number;
  amount: number;
  source_def_id: string;
}

interface Aggregate {
  source_name: string;
  amount: number;
}

export class RunObserver {
  private ring: Intake[] = [];
  private head = 0;
  private lastHitTick = -1;
  private lastHitAmount = 0;
  private lastHitSource = '';
  private peakSurge = 1;

  constructor() {
    for (let i = 0; i < RING; i++) this.ring.push({ tick: -1, amount: 0, source_def_id: '' });
  }

  reset(): void {
    for (let i = 0; i < RING; i++) {
      this.ring[i].tick = -1;
      this.ring[i].amount = 0;
      this.ring[i].source_def_id = '';
    }
    this.head = 0;
    this.lastHitTick = -1;
    this.lastHitAmount = 0;
    this.lastHitSource = '';
    this.peakSurge = 1;
  }

  /** From the `player_hit` event. Zero allocation: the ring slot is overwritten. */
  notePlayerHit(tick: number, amount: number, sourceDefId: string): void {
    const slot = this.ring[this.head];
    slot.tick = tick;
    slot.amount = amount;
    slot.source_def_id = sourceDefId;
    this.head = (this.head + 1) % RING;
    this.lastHitTick = tick;
    this.lastHitAmount = amount;
    this.lastHitSource = sourceDefId;
  }

  noteSurge(tier: number): void {
    if (tier > this.peakSurge) this.peakSurge = tier;
  }

  get peak(): number {
    return this.peakSurge;
  }

  /** All four §13.1.9 fields, always populated — unknown sources fall back to a label. */
  build(w: World): DefeatReport {
    const tick = w.tick;
    const killer = this.lastHitSource;

    const last10: Aggregate[] = [];
    for (let i = 0; i < RING; i++) {
      const s = this.ring[i];
      if (s.tick < 0 || tick - s.tick > WINDOW_TICKS) continue;
      const name = displayName(s.source_def_id);
      let found = false;
      for (let k = 0; k < last10.length; k++) {
        if (last10[k].source_name === name) {
          last10[k].amount += s.amount;
          found = true;
          break;
        }
      }
      if (!found) last10.push({ source_name: name, amount: s.amount });
    }
    last10.sort((a, b) => (b.amount === a.amount ? (a.source_name < b.source_name ? -1 : 1) : b.amount - a.amount));
    const top3 = last10.slice(0, 3);
    if (top3.length === 0) {
      top3.push({ source_name: 'Attrition', amount: Math.max(0, Math.round(w.counters.damage_taken)) });
    }

    const dashRemainingTicks = w.player.dash.cooldown_until - tick;
    const dashReady = dashRemainingTicks <= 0;

    let healthAlive = 0;
    const list = w.pickups.liveIndices();
    for (let i = 0; i < list.length; i++) {
      if (w.pickups.slots[list[i]].pickup_kind === 'health') healthAlive++;
    }

    return {
      killing_blow: {
        source_name: displayName(killer),
        damage: Math.max(0, Math.round(this.lastHitAmount)),
        at_time_s: (this.lastHitTick >= 0 ? this.lastHitTick : tick) / 60,
        pos: { x: w.player.pos.x, y: w.player.pos.y },
      },
      last_10s: top3,
      what_you_had: {
        surge_tier_lost: Math.max(0, this.peakSurge - w.player.lumensurge_tier),
        dash_ready: dashReady,
        dash_seconds_remaining: dashReady ? 0 : dashRemainingTicks / 60,
      },
      what_was_offered: {
        health_pickups_alive: healthAlive,
        counterplay: counterplayFor(killer),
      },
    };
  }
}

function displayName(defId: string): string {
  if (!defId) return 'Unknown source';
  const e = enemyById.get(defId);
  if (e) return e.display_name;
  const b = bossById.get(defId);
  if (b) return String(b.display_name);
  return humanizeId(defId);
}

/** §07 counterplay line, verbatim from `enemies.json`. */
function counterplayFor(defId: string): string {
  const e = defId ? enemyById.get(defId) : undefined;
  if (e && typeof e.counterplay === 'string' && e.counterplay.length > 0) return e.counterplay;
  return 'Dash through the contact frame; the i-frames cover the whole lunge.';
}
